import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid,
  Alert, Snackbar, InputAdornment, Tooltip, Tabs, Tab,
  CircularProgress, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';

// ── Auth helper ────────────────────────────────────────────────────────────
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ── Tab definitions ────────────────────────────────────────────────────────
const TAB_CONFIG = [
  { id: 'products',    label: 'Products',        icon: '🦐', api: 'products'    },
  { id: 'brands',      label: 'Brands',          icon: '🏷️', api: 'brands'      },
  { id: 'grades',      label: 'Grades',          icon: '📏', api: 'grades'      },
  { id: 'pack_sizes',  label: 'Pack Sizes',      icon: '📦', api: 'pack_sizes'  },
  { id: 'nav_mappings',label: 'NAV ID Mappings', icon: '🔗', api: 'nav_mappings'},
];

// ════════════════════════════════════════════════════════════════════════════
//  MasterManagement
// ════════════════════════════════════════════════════════════════════════════
function MasterManagement({ defaultTab = 0 }) {
  const [activeTab,   setActiveTab]   = useState(defaultTab);
  const [rows,        setRows]        = useState([]);
  const [allRows,     setAllRows]     = useState([]);   // unfiltered
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [dialogMode,  setDialogMode]  = useState('add');
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteOpen,  setDeleteOpen]  = useState(false);
  const [snackbar,    setSnackbar]    = useState({ open: false, message: '', severity: 'success' });

  // ── Pagination state ───────────────────────────────────────────────────
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ── Form state ─────────────────────────────────────────────────────────
  // Simple tables (products/brands/grades/pack_sizes)
  const [name,     setName]     = useState('');
  const [nameErr,  setNameErr]  = useState('');
  const [code,     setCode]     = useState('');   // products only — optional code

  // NAV ID Mappings — per-column inline filters
  const [navColFilters, setNavColFilters] = useState({
    nav_id: '', product: '', brand: '', grade: '', pack_size: '',
  });

  // NAV mapping fields
  const [navId,    setNavId]    = useState('');
  const [navProd,  setNavProd]  = useState('');
  const [navBrand, setNavBrand] = useState('');
  const [navGrade, setNavGrade] = useState('');
  const [navPack,  setNavPack]  = useState('');
  const [navIdErr, setNavIdErr] = useState('');
  const [navProdErr,setNavProdErr] = useState('');

  // Dropdown options for NAV mapping (loaded from other tables)
  const [optProducts,  setOptProducts]  = useState([]);
  const [optBrands,    setOptBrands]    = useState([]);
  const [optGrades,    setOptGrades]    = useState([]);
  const [optPackSizes, setOptPackSizes] = useState([]);

  const currentTab = TAB_CONFIG[activeTab];
  const isNavTab   = currentTab.id === 'nav_mappings';
  const isProductTab = currentTab.id === 'products';

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (tabIndex = activeTab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/masters.php`, {
        params: { type: TAB_CONFIG[tabIndex].api },
        headers: getAuthHeader(),
      });
      if (res.data.success) {
        setAllRows(res.data.data || []);
        setRows(res.data.data || []);
      }
    } catch (err) {
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Fetch dropdown options for NAV mapping tab
  const fetchNavOptions = useCallback(async () => {
    try {
      const [p, b, g, ps] = await Promise.all([
        axios.get(`${API_BASE_URL}/masters.php`, { params: { type: 'products'   }, headers: getAuthHeader() }),
        axios.get(`${API_BASE_URL}/masters.php`, { params: { type: 'brands'     }, headers: getAuthHeader() }),
        axios.get(`${API_BASE_URL}/masters.php`, { params: { type: 'grades'     }, headers: getAuthHeader() }),
        axios.get(`${API_BASE_URL}/masters.php`, { params: { type: 'pack_sizes' }, headers: getAuthHeader() }),
      ]);
      if (p.data.success)  setOptProducts(p.data.data   || []);
      if (b.data.success)  setOptBrands(b.data.data     || []);
      if (g.data.success)  setOptGrades(g.data.data     || []);
      if (ps.data.success) setOptPackSizes(ps.data.data || []);
    } catch (err) {
      console.error('Failed to load options', err);
    }
  }, []);

  useEffect(() => {
    setSearch('');
    setPage(0);
    setNavColFilters({ nav_id: '', product: '', brand: '', grade: '', pack_size: '' });
    fetchData(activeTab);
    if (TAB_CONFIG[activeTab].id === 'nav_mappings') fetchNavOptions();
  }, [activeTab]);

  // ── Search filter (+ NAV per-column filters) ─────────────────────────────
  useEffect(() => {
    let result = allRows;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(r =>
        Object.values(r).some(v => v && String(v).toLowerCase().includes(s))
      );
    }
    // NAV ID Mappings: narrow by each column's inline filter
    if (isNavTab) {
      const cf = navColFilters;
      const match = (val, q) => !q || String(val ?? '').toLowerCase().includes(q.toLowerCase());
      result = result.filter(r =>
        match(r.nav_id, cf.nav_id) &&
        match(r.product, cf.product) &&
        match(r.brand, cf.brand) &&
        match(r.grade, cf.grade) &&
        match(r.pack_size, cf.pack_size)
      );
    }
    setRows(result);
    setPage(0); // reset to first page on every filter change
  }, [search, allRows, navColFilters, isNavTab]);

  // ── Snackbar ────────────────────────────────────────────────────────────
  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Export to Excel ──────────────────────────────────────────────────────
  const exportToExcel = () => {
    if (rows.length === 0) { showSnackbar('No data to export', 'warning'); return; }

    // Build export rows based on current tab
    const exportData = rows.map((row, idx) => {
      if (isNavTab) {
        return {
          '#':          idx + 1,
          'NAV ID':     row.nav_id    || '',
          'Product':    row.product   || '',
          'Brand':      row.brand     || '',
          'Grade':      row.grade     || '',
          'Pack Size':  row.pack_size || '',
          'Created':    row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
        };
      }
      if (isProductTab) {
        return {
          '#':        idx + 1,
          'Code':     row.code || '',
          'Name':     row.name || '',
          'Created':  row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
        };
      }
      return {
        '#':        idx + 1,
        'Name':     row.name || '',
        'Created':  row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto column widths
    const colWidths = Object.keys(exportData[0]).map(k => ({
      wch: Math.max(k.length, ...exportData.map(r => String(r[k] || '').length)) + 2
    }));
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, currentTab.label);

    const fileName = `${currentTab.label.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    showSnackbar(`✅ Exported ${rows.length} records to ${fileName}`);
  };

  // ── Open dialogs ────────────────────────────────────────────────────────
  const openAdd = () => {
    setDialogMode('add');
    setSelectedRow(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode('edit');
    setSelectedRow(row);
    if (isNavTab) {
      setNavId(row.nav_id    || '');
      setNavProd(row.product  || '');
      setNavBrand(row.brand   || '');
      setNavGrade(row.grade   || '');
      setNavPack(row.pack_size || '');
      setNavIdErr('');
      setNavProdErr('');
    } else {
      setName(row.name || '');
      setCode(row.code || '');
      setNameErr('');
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setName(''); setNameErr(''); setCode('');
    setNavId(''); setNavProd(''); setNavBrand('');
    setNavGrade(''); setNavPack('');
    setNavIdErr(''); setNavProdErr('');
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedRow(null);
    resetForm();
  };

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Validate
    if (isNavTab) {
      let valid = true;
      if (!navId.trim())   { setNavIdErr('NAV ID is required');   valid = false; }
      if (!navProd.trim()) { setNavProdErr('Product is required'); valid = false; }
      if (!valid) return;
    } else {
      if (!name.trim()) { setNameErr('Name is required'); return; }
    }

    const payload = {
      type:   currentTab.api,
      action: dialogMode === 'add' ? 'create' : 'update',
      data:   isNavTab
        ? { nav_id: navId, product: navProd, brand: navBrand, grade: navGrade, pack_size: navPack }
        : isProductTab
          ? { name, code }
          : { name },
      ...(dialogMode === 'edit' && { id: selectedRow.id }),
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/masters.php`, payload, { headers: getAuthHeader() });
      if (res.data.success) {
        showSnackbar(dialogMode === 'add' ? 'Record created' : 'Record updated');
        closeDialog();
        fetchData(activeTab);
      } else {
        showSnackbar(res.data.message || 'Operation failed', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Server error', 'error');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────
  const openDelete = (row) => { setSelectedRow(row); setDeleteOpen(true); };

  const handleDelete = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/masters.php`, {
        type: currentTab.api, action: 'delete', id: selectedRow.id,
      }, { headers: getAuthHeader() });
      if (res.data.success) {
        showSnackbar('Deleted successfully');
        setDeleteOpen(false);
        fetchData(activeTab);
      } else {
        showSnackbar(res.data.message || 'Delete failed', 'error');
      }
    } catch (err) {
      showSnackbar('Server error', 'error');
    }
  };

  // ── Row label for delete confirm ─────────────────────────────────────
  const rowLabel = (row) => row?.name || row?.nav_id || 'this record';

  // ════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ p: 3 }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          🗂️ Master Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />}
            onClick={() => fetchData(activeTab)}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Refresh
          </Button>
          <Button variant="outlined" color="success" startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            disabled={rows.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Export Excel
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
            ADD {currentTab.label.toUpperCase().replace(/S$/, '')}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>

        {/* ── Tabs ── */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          variant="scrollable" scrollButtons="auto"
        >
          {TAB_CONFIG.map((t, i) => (
            <Tab key={t.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <span>{t.icon}</span><span>{t.label}</span>
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 52 }}
            />
          ))}
        </Tabs>

        {/* ── Search ── */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: '1px solid', borderColor: 'divider', background: '#fafbfc' }}>
          <TextField size="small" placeholder={`Search ${currentTab.label}...`}
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {rows.length} {rows.length === 1 ? 'record' : 'records'}
            {search && ` matching "${search}"`}
          </Typography>
        </Box>

        {/* ── Table ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f', width: 50 }}>#</TableCell>
                  {isNavTab ? (
                    <>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>NAV ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Brand</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Grade</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Pack Size</TableCell>
                    </>
                  ) : isProductTab ? (
                    <>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f', width: 160 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Name</TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f' }}>Name</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f', width: 120 }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#6b7a8f', width: 100 }}>Actions</TableCell>
                </TableRow>
                {/* NAV ID Mappings — per-column inline filter row */}
                {isNavTab && (
                  <TableRow sx={{ background: '#fff' }}>
                    <TableCell sx={{ py: 0.5 }} />
                    {['nav_id', 'product', 'brand', 'grade', 'pack_size'].map((col) => (
                      <TableCell key={col} sx={{ py: 0.5 }}>
                        <TextField
                          variant="standard"
                          size="small"
                          fullWidth
                          placeholder="Filter…"
                          value={navColFilters[col]}
                          onChange={e => setNavColFilters(prev => ({ ...prev, [col]: e.target.value }))}
                          InputProps={{ sx: { fontSize: 12 } }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ py: 0.5 }} />
                    <TableCell sx={{ py: 0.5 }} align="right">
                      {Object.values(navColFilters).some(Boolean) && (
                        <Tooltip title="Clear column filters">
                          <IconButton size="small" onClick={() => setNavColFilters({ nav_id: '', product: '', brand: '', grade: '', pack_size: '' })}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isNavTab ? 8 : isProductTab ? 5 : 4} align="center"
                      sx={{ py: 6, color: 'text.secondary' }}>
                      {search ? `No records matching "${search}"` : 'No records yet. Click ADD to create one.'}
                    </TableCell>
                  </TableRow>
                ) : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                  <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ color: '#9aa5b4', fontSize: 12 }}>{page * rowsPerPage + idx + 1}</TableCell>
                    {isNavTab ? (
                      <>
                        <TableCell sx={{ fontWeight: 700 }}>{row.nav_id}</TableCell>
                        <TableCell>{row.product}</TableCell>
                        <TableCell>{row.brand  || <span style={{ color: '#9aa5b4' }}>—</span>}</TableCell>
                        <TableCell>{row.grade  || <span style={{ color: '#9aa5b4' }}>—</span>}</TableCell>
                        <TableCell>{row.pack_size || <span style={{ color: '#9aa5b4' }}>—</span>}</TableCell>
                      </>
                    ) : isProductTab ? (
                      <>
                        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {row.code || <span style={{ color: '#9aa5b4' }}>—</span>}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                    )}
                    <TableCell sx={{ color: '#9aa5b4', fontSize: 11 }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => openDelete(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Pagination ── */}
        {rows.length > 0 && (
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 75, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              background: '#fafbfc',
              '.MuiTablePagination-toolbar': { minHeight: 44 },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: 12, color: '#6b7a8f',
              },
            }}
          />
        )}
      </Paper>

      {/* ════ ADD / EDIT DIALOG ════ */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{dialogMode === 'add' ? '➕ Add' : '✏️ Edit'} {currentTab.label.replace(/s$/i, '')}</span>
          <IconButton size="small" onClick={closeDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {isNavTab ? (
            /* NAV ID MAPPING FORM */
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="NAV ID" required
                  value={navId} onChange={e => { setNavId(e.target.value.toUpperCase()); setNavIdErr(''); }}
                  error={!!navIdErr} helperText={navIdErr} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required error={!!navProdErr}>
                  <InputLabel>Product</InputLabel>
                  <Select value={navProd} label="Product"
                    onChange={e => { setNavProd(e.target.value); setNavProdErr(''); }}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {optProducts.map(p => <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>)}
                  </Select>
                  {navProdErr && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{navProdErr}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Brand</InputLabel>
                  <Select value={navBrand} label="Brand" onChange={e => setNavBrand(e.target.value)}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {optBrands.map(b => <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grade</InputLabel>
                  <Select value={navGrade} label="Grade" onChange={e => setNavGrade(e.target.value)}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {optGrades.map(g => <MenuItem key={g.id} value={g.name}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Pack Size</InputLabel>
                  <Select value={navPack} label="Pack Size" onChange={e => setNavPack(e.target.value)}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {optPackSizes.map(p => <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          ) : isProductTab ? (
            /* PRODUCT FORM — Code (optional) + Name */
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" label="Product Code"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="e.g. CEZP"
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label="Product Name" required autoFocus
                  value={name}
                  onChange={e => { setName(e.target.value); setNameErr(''); }}
                  error={!!nameErr} helperText={nameErr}
                />
              </Grid>
            </Grid>
          ) : (
            /* SIMPLE NAME FORM */
            <TextField fullWidth size="small"
              label={`${currentTab.label.replace(/s$/i, '')} Name`}
              required autoFocus
              value={name}
              onChange={e => { setName(e.target.value); setNameErr(''); }}
              error={!!nameErr} helperText={nameErr}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={closeDialog} startIcon={<CloseIcon />}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />}>
            {dialogMode === 'add' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════ DELETE CONFIRM ════ */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{rowLabel(selectedRow)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ════ SNACKBAR ════ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default MasterManagement;
